import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  TrendingUp, Gift, Megaphone, LayoutDashboard, LogOut, Crown,
  ArrowUpRight, Lock, Sparkles, Globe,
  Inbox, Settings, Archive, ArchiveRestore, Save, Mail, Phone, Smartphone, Clock, AtSign
} from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const WHOP_URL = "https://whop.com/viralcrew-tiktok/viralcrew-pro/";

function navItems(user, t) {
  return [
    { id: "overview", label: t.dashboard.overview, icon: LayoutDashboard },
    { id: "trends", label: t.dashboard.trends, icon: TrendingUp },
    { id: "campaigns", label: t.dashboard.campaigns, icon: Megaphone },
    { id: "samples", label: t.dashboard.samples, icon: Gift },
    ...(user?.role === "admin" ? [{ id: "leads", label: "Anfragen", icon: Inbox }] : []),
    { id: "settings", label: "Profil", icon: Settings },
  ];
}

function MobileNav({ active, setActive, user }) {
  const { t } = useI18n();
  return (
    <div className="md:hidden border-b border-white/10 bg-[#080808] overflow-x-auto">
      <div className="flex gap-1 px-3 py-2 min-w-max">
        {navItems(user, t).map((it) => (
          <button
            key={it.id}
            onClick={() => setActive(it.id)}
            data-testid={`mobilenav-${it.id}`}
            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition ${
              active === it.id ? "bg-[#FF0050]/10 text-white border border-[#FF0050]/30" : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <it.icon className="h-3.5 w-3.5" />{it.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function Sidebar({ active, setActive }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const { user, logout } = useAuth();

  const items = navItems(user, t);

  return (
    <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-white/10 bg-[#080808] p-5 sticky top-0 h-screen">
      <Link to="/" className="flex items-center gap-2.5 mb-10">
        <Logo size={28} />
        <span className="font-display font-black tracking-tight text-lg">ViralCrew</span>
      </Link>

      <nav className="flex-1 space-y-1">
        {items.map((it) => (
          <button
            key={it.id}
            onClick={() => setActive(it.id)}
            data-testid={`sidebar-${it.id}`}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
              active === it.id
                ? "bg-[#FF0050]/10 text-white border border-[#FF0050]/30"
                : "text-zinc-400 hover:text-white hover:bg-white/5"
            }`}
          >
            <it.icon className="h-4 w-4" />
            {it.label}
          </button>
        ))}
      </nav>

      <div className="border-t border-white/10 pt-4 mt-4">
        <div className="px-3 py-3 rounded-lg bg-gradient-to-br from-[#1a0a12] to-transparent border border-[#FF0050]/30 mb-3">
          <div className="flex items-center gap-2 mb-1">
            <Crown className="h-3.5 w-3.5 text-[#FF0050]" />
            <span className="text-xs font-mono uppercase tracking-wider text-[#FF0050]">{user?.plan === "pro" ? t.dashboard.proActive : "Free"}</span>
          </div>
          {user?.plan !== "pro" && (
            <a
              href={WHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sidebar-upgrade-btn"
              className="block text-xs text-white hover:text-[#FF0050] mt-1"
            >
              {t.dashboard.upgrade} →
            </a>
          )}
        </div>
        <button
          onClick={async () => { await logout(); nav("/"); }}
          data-testid="dashboard-logout-btn"
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5"
        >
          <LogOut className="h-4 w-4" /> {t.nav.logout}
        </button>
      </div>
    </aside>
  );
}

function StatCard({ label, value, accent }) {
  return (
    <div className="bg-[#0A0A0A] border border-white/10 rounded-2xl p-6">
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
      <div className={`font-display font-black text-3xl md:text-4xl tracking-tighter ${accent || "text-white"}`}>{value}</div>
    </div>
  );
}

function Overview() {
  const { t } = useI18n();
  const { user } = useAuth();
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
      <div>
        <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">
          {t.dashboard.welcome}, <span className="gradient-text">{user?.name?.split(" ")[0] || ""}</span>
        </h1>
        <p className="text-zinc-400 mt-2">
          {user?.role === "brand" ? "Manage your campaigns and creator partnerships." : "Track your earnings, trends and brand deals."}
        </p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label={t.dashboard.stats.earnings} value="€2,430" accent="text-[#FF0050]" />
        <StatCard label={t.dashboard.stats.views} value="1.2M" accent="text-[#00F2FE]" />
        <StatCard label={t.dashboard.stats.campaigns} value="3" />
        <StatCard label={t.dashboard.stats.samples} value={user?.plan === "pro" ? "5" : "0"} />
      </div>

      {user?.plan !== "pro" && (
        <a href={WHOP_URL} target="_blank" rel="noopener noreferrer" className="block">
          <div className="relative overflow-hidden rounded-2xl border border-[#FF0050]/40 bg-gradient-to-br from-[#1a0a12] via-[#0A0A0A] to-[#050505] p-8 group hover:border-[#FF0050] transition glow-pink">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="h-4 w-4 text-[#FF0050]" />
                  <span className="text-xs font-mono uppercase tracking-wider text-[#FF0050]">{t.dashboard.upgrade}</span>
                </div>
                <h3 className="font-display font-black text-2xl md:text-3xl mb-2">Unlock free samples, exclusive deals & higher commissions.</h3>
                <p className="text-zinc-400 text-sm">€19.99/month · cancel anytime · billed via Whop</p>
              </div>
              <div className="hidden md:flex items-center gap-2 bg-[#FF0050] text-white px-5 py-3 rounded-full font-bold group-hover:translate-x-1 transition">
                Go Pro <ArrowUpRight className="h-4 w-4" />
              </div>
            </div>
          </div>
        </a>
      )}
    </motion.div>
  );
}

function Trends() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/trends").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">{t.dashboard.trends}</h1>
        <p className="text-zinc-400 mt-2">Live signal — updated every 4 hours.</p>
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0F0F10] text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-4">Product</th>
              <th className="text-left p-4">Category</th>
              <th className="text-left p-4">Growth</th>
              <th className="text-left p-4">Score</th>
              <th className="text-left p-4">Commission</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                <td className="p-4 font-medium flex items-center gap-3">
                  <span className="text-xl">{it.thumbnail}</span>{it.product}
                </td>
                <td className="p-4 text-zinc-400">{it.category}</td>
                <td className="p-4 text-[#00E676] font-mono">{it.growth}</td>
                <td className="p-4 font-mono">{it.score}</td>
                <td className="p-4 text-[#FF0050] font-mono">{it.commission}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {user?.plan !== "pro" && (
        <p className="text-xs font-mono uppercase tracking-wider text-zinc-500">Free preview · upgrade for live alerts & 20+ niches</p>
      )}
    </motion.div>
  );
}

function Campaigns() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/campaigns").then((r) => setItems(r.data.items)).catch(() => {});
  }, []);
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div>
        <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">{t.dashboard.campaigns}</h1>
        <p className="text-zinc-400 mt-2">Active brand deals you can join right now.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        {items.map((c) => (
          <div key={c.id} className={`relative rounded-2xl border p-6 ${c.locked ? "border-white/5 bg-[#08080A] opacity-70" : "border-white/10 bg-[#0A0A0A] hover:border-[#FF0050]/40 transition"}`}>
            {c.locked && (
              <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FF0050]/10 border border-[#FF0050]/30 text-[#FF0050] text-[10px] font-mono uppercase">
                <Lock className="h-3 w-3" /> Pro
              </div>
            )}
            <div className="text-3xl mb-3">{c.image}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{c.brand}</div>
            <h3 className="font-display font-bold text-xl mb-3">{c.product}</h3>
            <div className="flex items-center gap-4 text-sm">
              <div><span className="text-zinc-500">Payout:</span> <span className="text-white font-mono">{c.payout}</span></div>
              <div><span className="text-zinc-500">Commission:</span> <span className="text-[#FF0050] font-mono">{c.commission}</span></div>
            </div>
            {c.locked && <p className="text-xs text-zinc-500 mt-4">{t.dashboard.lockedPro}</p>}
          </div>
        ))}
      </div>
    </motion.div>
  );
}

function Samples() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [locked, setLocked] = useState(false);
  const refresh = () => api.get("/samples").then((r) => { setItems(r.data.items || []); setLocked(!!r.data.locked); }).catch(() => {});
  useEffect(() => { refresh(); }, []);

  const requestSample = async (productId) => {
    if (user?.plan !== "pro") {
      toast.error(t.dashboard.onlyPro);
      return;
    }
    try {
      await api.post("/samples/request", { product_id: productId, address: "Your saved address" });
      toast.success("Sample request placed ✨");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const catalog = [
    { id: "p1", name: "Glow Vitamin C Serum", brand: "GlowLab", emoji: "🌟" },
    { id: "p2", name: "Wireless Earbuds Pro", brand: "Nova Audio", emoji: "🎧" },
    { id: "p3", name: "Cordless Hair Curler", brand: "WaveCo", emoji: "💇" },
    { id: "p4", name: "Memory Foam Pillow", brand: "SoftHome", emoji: "🛏" },
  ];

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">{t.dashboard.samples}</h1>
          <p className="text-zinc-400 mt-2">Order free product samples — shipped to your door.</p>
        </div>
        {locked && (
          <a href={WHOP_URL} target="_blank" rel="noopener noreferrer" className="bg-[#FF0050] hover:bg-[#E00046] text-white font-bold rounded-full px-5 py-2.5 text-sm">
            {t.dashboard.upgrade}
          </a>
        )}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {catalog.map((p) => (
          <div key={p.id} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5 flex flex-col">
            <div className="text-4xl mb-3">{p.emoji}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{p.brand}</div>
            <h3 className="font-display font-bold text-base mb-4 flex-1">{p.name}</h3>
            <button
              onClick={() => requestSample(p.id)}
              disabled={locked}
              data-testid={`request-sample-${p.id}`}
              className="bg-[#FF0050] hover:bg-[#E00046] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold rounded-full px-4 py-2 text-sm transition"
            >
              {locked ? <span className="inline-flex items-center gap-1"><Lock className="h-3 w-3" /> Pro</span> : t.dashboard.requestSample}
            </button>
          </div>
        ))}
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6">
        <h3 className="font-display font-bold text-lg mb-4">Your requests</h3>
        {items.length === 0 ? (
          <p className="text-zinc-500 text-sm">{t.dashboard.noSamples}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {items.map((it, i) => (
              <li key={i} className="flex items-center justify-between border-b border-white/5 py-2 last:border-0">
                <span className="font-mono text-zinc-300">{it.product_id}</span>
                <span className="text-xs font-mono uppercase tracking-wider text-[#00F2FE]">{it.status}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}

function Leads() {
  const [items, setItems] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get(`/admin/leads?include_archived=${showArchived}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [showArchived]);

  const toggleArchive = async (lead) => {
    try {
      await api.patch(`/admin/leads/${lead.lead_id}`, { archived: !lead.archived });
      toast.success(lead.archived ? "Wiederhergestellt" : "Archiviert");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Anfragen</h1>
          <p className="text-zinc-400 mt-2">Alle Kontaktanfragen aus dem Formular – nur für dich als Admin sichtbar.</p>
        </div>
        <button
          onClick={() => setShowArchived((v) => !v)}
          data-testid="toggle-archived-btn"
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition"
        >
          <Archive className="h-4 w-4" />{showArchived ? "Aktive anzeigen" : "Archiv anzeigen"}
        </button>
      </div>

      {loading ? (
        <p className="text-zinc-500 text-sm font-mono">lädt…</p>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-10 text-center text-zinc-500" data-testid="leads-empty">
          {showArchived ? "Keine archivierten Anfragen." : "Noch keine Anfragen eingegangen."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="leads-list">
          {items.map((l) => (
            <div key={l.lead_id} data-testid={`lead-card-${l.lead_id}`} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-lg">{l.name}</span>
                    <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-zinc-400">{l.type}</span>
                    {l.archived && <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">archiviert</span>}
                  </div>
                  <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-zinc-300">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-2 hover:text-white truncate"><Mail className="h-3.5 w-3.5 text-zinc-500 shrink-0" />{l.email}</a>
                    {l.handle && <span className="inline-flex items-center gap-2"><AtSign className="h-3.5 w-3.5 text-zinc-500 shrink-0" />{l.handle}</span>}
                    {l.phone && <a href={`tel:${l.phone}`} className="inline-flex items-center gap-2 hover:text-white"><Phone className="h-3.5 w-3.5 text-zinc-500 shrink-0" />{l.phone}</a>}
                    {l.mobile && <a href={`tel:${l.mobile}`} className="inline-flex items-center gap-2 hover:text-white"><Smartphone className="h-3.5 w-3.5 text-zinc-500 shrink-0" />{l.mobile}</a>}
                  </div>
                  {l.message && <p className="mt-3 text-sm text-zinc-400 bg-black/40 rounded-lg p-3 border border-white/5 whitespace-pre-line">{l.message}</p>}
                  <div className="mt-2 text-[11px] font-mono text-zinc-600 inline-flex items-center gap-1.5"><Clock className="h-3 w-3" />{l.created_at ? new Date(l.created_at).toLocaleString("de-DE") : ""}</div>
                </div>
                <button
                  onClick={() => toggleArchive(l)}
                  data-testid={`archive-lead-${l.lead_id}`}
                  title={l.archived ? "Wiederherstellen" : "Archivieren"}
                  className="shrink-0 h-9 w-9 rounded-lg border border-white/10 text-zinc-400 hover:text-white hover:border-white/30 flex items-center justify-center transition"
                >
                  {l.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}

function SettingsPanel() {
  const { user, refreshMe } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const inputCls = "w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#00F2FE] transition";

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      await api.patch("/auth/me", { name });
      await refreshMe();
      toast.success("Profil aktualisiert ✨");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingName(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { toast.error("Die Passwörter stimmen nicht überein."); return; }
    setSavingPw(true);
    try {
      await api.patch("/auth/me", { current_password: cur, new_password: pw });
      toast.success("Passwort geändert ✨");
      setCur(""); setPw(""); setPw2("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 max-w-xl">
      <div>
        <h1 className="font-display font-black tracking-tighter text-3xl md:text-4xl">Profil</h1>
        <p className="text-zinc-400 mt-2">Verwalte deine Kontodaten und dein Passwort.</p>
      </div>

      <form onSubmit={saveProfile} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Kontodaten</h3>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">E-Mail</label>
          <input value={user?.email || ""} disabled className={`${inputCls} mt-1 opacity-60`} data-testid="settings-email-input" />
        </div>
        <div>
          <label className="text-xs font-mono uppercase tracking-wider text-zinc-500">Name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name-input" className={`${inputCls} mt-1`} />
        </div>
        <button type="submit" disabled={savingName} data-testid="settings-save-name-btn"
          className="inline-flex items-center gap-2 bg-[#FF0050] hover:bg-[#E00046] disabled:opacity-60 text-white font-bold rounded-full px-5 py-2.5 text-sm transition">
          <Save className="h-4 w-4" />{savingName ? "Speichert…" : "Speichern"}
        </button>
      </form>

      <form onSubmit={changePassword} className="rounded-2xl border border-white/10 bg-[#0A0A0A] p-6 space-y-4">
        <h3 className="font-display font-bold text-lg">Passwort ändern</h3>
        <input type="password" placeholder="Aktuelles Passwort" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="settings-current-password" className={inputCls} />
        <input type="password" placeholder="Neues Passwort (min. 8 Zeichen)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="settings-new-password" className={inputCls} />
        <input type="password" placeholder="Neues Passwort bestätigen" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="settings-confirm-password" className={inputCls} />
        <button type="submit" disabled={savingPw} data-testid="settings-change-password-btn"
          className="inline-flex items-center gap-2 bg-white text-black font-bold rounded-full px-5 py-2.5 text-sm hover:bg-zinc-200 transition disabled:opacity-60">
          {savingPw ? "Ändert…" : "Passwort ändern"}
        </button>
      </form>
    </motion.div>
  );
}

export default function Dashboard() {
  const { user, loading } = useAuth();
  const { lang, setLang } = useI18n();
  const nav = useNavigate();
  const [active, setActive] = useState("overview");

  useEffect(() => {
    if (!loading && user === false) nav("/login", { replace: true });
  }, [loading, user, nav]);

  if (loading || user === null || user === false) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-zinc-400 font-mono text-sm">
        loading...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      <Sidebar active={active} setActive={setActive} />
      <main className="flex-1 min-w-0">
        <div className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
          <div className="px-6 md:px-10 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 md:hidden">
              <Logo size={26} />
              <span className="font-display font-black tracking-tight">ViralCrew</span>
            </div>
            <div className="flex items-center gap-3 ml-auto">
              <button
                onClick={() => setLang(lang === "de" ? "en" : "de")}
                className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white border border-white/10 rounded-full px-3 py-1.5"
              >
                <Globe className="h-3.5 w-3.5" />{lang.toUpperCase()}
              </button>
              <div className="flex items-center gap-2.5 pl-3 border-l border-white/10">
                {user.picture ? (
                  <img src={user.picture} alt="" className="h-8 w-8 rounded-full" />
                ) : (
                  <span className="h-8 w-8 rounded-full bg-[#FF0050] text-white font-bold flex items-center justify-center text-sm">{user.name?.[0]?.toUpperCase() || "V"}</span>
                )}
                <div className="hidden sm:block">
                  <div className="text-sm font-semibold leading-tight">{user.name}</div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">{user.role} · {user.plan}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <MobileNav active={active} setActive={setActive} user={user} />

        <div className="px-6 md:px-10 py-10 max-w-6xl">
          {active === "overview" && <Overview />}
          {active === "trends" && <Trends />}
          {active === "campaigns" && <Campaigns />}
          {active === "samples" && <Samples />}
          {active === "leads" && user.role === "admin" && <Leads />}
          {active === "settings" && <SettingsPanel />}
        </div>
      </main>
    </div>
  );
}
